import { catchError, from, map, Observable, switchMap, tap } from 'rxjs';
import { AxiosError, AxiosResponse } from 'axios';
import { UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import fileDownload from 'js-file-download';

import { suppliersUnitsStore } from '@store/suppliers/units/suppliers-units.store';
import { Pagination } from '@store/common/pagination.model';
import { SuppliersUnitsFilters, SuppliersUnitsPagination } from '@store/suppliers/units/suppliers-units.model';
import { getSuppliersUnitsDataSource } from '@store/suppliers/units/suppliers-units.requests';
import { ManageContact, ManageOrganisation, ManageOrganisationUnit } from '@store/common/organisation.model';

import { TableConfig } from '@constants/table.constants';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';
import { GridSortItem } from '@mui/x-data-grid-pro';
import { GlobalStatus } from '@store/common/globalStatus.enum';
import { Array } from '@utils/extensions/array.extension';
import { TableFilter } from '@store/common/tableFilter.model';
import { OrganisationStatus } from '@store/common/organisation.enums';

export class SuppliersUnitsService {
  store = suppliersUnitsStore;

  resetStore = () => this.store.reset();

  setStatus = (status?: GlobalStatus) => this.store.update((state) => ({ ...state, status }));
  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (supplierId?: string) => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: SuppliersUnitsFilters.initTableFilters(supplierId) },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  searchSuppliersUnits = (search?: string, supplierIds: (string | number)[] = []): Observable<SuppliersUnitsPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETSuppliersUnits(),
      params: {
        supplierOrganisationIds: Array.formatJson(Array.mapNotNull(supplierIds)),
        searchString: search || undefined,
        sortBy: 'organisationUnit.name',
        orderBy: 'ASC',
        page: 1,
        limit: TableConfig.pagination,
        status: OrganisationStatus.ACTIVE
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersUnitsPagination>) => {
          return response.data;
        }),
      );
  };

  getSuppliersUnits = (): Observable<SuppliersUnitsPagination> => {
    const { status, filters: { search, tableFilters }, sort, tablePagination } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliersUnits(),
      params: {
        ...requestFilters,
        status,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersUnitsPagination>) => {
          return response.data;
        }),
        tap((suppliersUnitsPagination) => {
          const paginationData = Pagination.toPaginationData(suppliersUnitsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(suppliersUnitsPagination.data),
            upsertEntitiesById(suppliersUnitsPagination.data.map((suppliersUnit) => suppliersUnit.id), {
              updater: (suppliersUnitUi) => suppliersUnitUi,
              creator: (suppliersUnitId) => ({ id: suppliersUnitId, selected: false }),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, suppliersUnitsPagination.data.map((suppliersUnit) => suppliersUnit.id)),
            getSuppliersUnitsDataSource.setSuccess(),
          );
        }),
        getSuppliersUnitsDataSource.trackRequestStatus(),
      );
  };

  getSuppliersUnitsCSV = (): Observable<Blob> => {
    const { status, filters: { search, tableFilters }, sort } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliersUnitsCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        status,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => {
          return response.data;
        }),
        tap((data) => {
          const date = new Date();
          const formattedDate =
            date.getDate() + '_' + (date.getMonth() + 1) + '_' + date.getFullYear();
          fileDownload(data, `supplier_organisation${!requestFilters.supplierOrganisationIds ? 's' : ''}_units_${formattedDate}.csv`);
        }),
      );
  };

  updateSuppliersUnitsStatusInList = (suppliersUnitId: number | string, status: GlobalStatus): Observable<SuppliersUnitsPagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHSuppliersUnitStatus(suppliersUnitId),
      data: { status },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSuppliersUnits()),
      );
  };

  deleteSuppliersUnitInList = (suppliersUnitId: number | string): Observable<SuppliersUnitsPagination> => {
    return from(APIAxios(APIRoutes.DELETESuppliersUnit(suppliersUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSuppliersUnits()),
      );
  };

  getSupplierUnitById = (supplierUnitId: string | number): Observable<ManageOrganisationUnit> => {
    return from(APIAxios(APIRoutes.GETSupplierUnitById(supplierUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  updateSuppliersUnitsStatus = (suppliersUnitId: number | string, status: GlobalStatus): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.PATCHSuppliersUnitStatus(suppliersUnitId),
      data: { status },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createSupplierUnit = (supplierId: string | number, data: ManageOrganisationUnit): Observable<ManageOrganisationUnit> => {
    return from(APIAxios({
      ...APIRoutes.POSTSuppliersUnit(supplierId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  updateSupplierUnitById = (supplierUnitId: string | number, data: ManageOrganisationUnit): Observable<ManageOrganisationUnit> => {
    return from(APIAxios({
      ...APIRoutes.PUTSupplierUnitById(supplierUnitId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisationUnit>) => {
          return response.data;
        }),
      );
  };

  deleteSuppliersUnit = (suppliersUnitId: number | string): Observable<AxiosResponse> => {
    return from(APIAxios(APIRoutes.DELETESuppliersUnit(suppliersUnitId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createSuppliersUnitContact = (suppliersUnitId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTSuppliersUnitContact(suppliersUnitId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierUnitById(suppliersUnitId)),
      );
  };

  updateSuppliersUnitContact = (suppliersUnitId: number | string, contactId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTSuppliersUnitContactById(suppliersUnitId, contactId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierUnitById(suppliersUnitId)),
      );
  };

  deleteSuppliersUnitContact = (suppliersUnitId: number | string, contactId: number | string): Observable<ManageOrganisation> => {
    return from(APIAxios(APIRoutes.DELETESuppliersUnitContact(suppliersUnitId, contactId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierUnitById(suppliersUnitId)),
      );
  };

  setSelectedSuppliersUnits = (suppliersUnitIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (suppliersUnitsUI) => ({ ...suppliersUnitsUI, selected: suppliersUnitIds.includes(suppliersUnitsUI.id) }),
        { ref: UIEntitiesRef },
      ),
    );
  };
}

export const suppliersUnitsService = new SuppliersUnitsService();
